import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../shared/api/errors';
import { Button } from '../../../shared/ui/button';
import { Checkbox } from '../../../shared/ui/checkbox';
import { FieldLabel } from '../../../shared/ui/field-label';
import { Input } from '../../../shared/ui/input';
import { Banner } from '../../../shared/ui/layout';
import { Modal } from '../../../shared/ui/modal';
import { Note } from '../../../shared/ui/note';
import { ShortId } from '../../../shared/ui/table';
import {
  VerifySteps,
  applyVersion,
  checkSample,
  verifySteps,
  type TAuthPresetSpec,
  type TAuthPresetUsage,
} from '../../../entities/auth-preset';
import { listSources } from '../../../entities/source';

const RECHECK_FAILED = 'auth_presets.sample_recheck_failed';

const refused = (value: unknown): boolean =>
  value instanceof ApiError && value.code === RECHECK_FAILED;

export const MoveSources = ({
  presetId,
  version,
  spec,
  usage,
}: {
  presetId: string;
  version: number;
  spec: TAuthPresetSpec | undefined;
  usage: readonly TAuthPresetUsage[];
}) => {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [secret, setSecret] = useState('');
  const queryClient = useQueryClient();

  const sources = useQuery({ queryKey: ['sources'], queryFn: listSources, enabled: open });
  const nameOf = (sourceId: string) =>
    sources.data?.data.find((source) => source.id === sourceId)?.name ?? null;

  const move = useMutation({
    mutationFn: (acknowledge: boolean) =>
      applyVersion(presetId, version, {
        sourceAuthMethodIds: picked,
        ...(secret ? { secret } : {}),
        acknowledgeSampleMismatch: acknowledge,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['auth-preset-usage', presetId] });
      void queryClient.invalidateQueries({ queryKey: ['auth-preset-versions-all', presetId] });
      void queryClient.invalidateQueries({ queryKey: ['auth-presets'] });
      setOpen(false);
      setPicked([]);
    },
  });

  const stopped = refused(move.error);

  const recheck = useQuery({
    queryKey: ['auth-preset-recheck', presetId, version, secret !== ''],
    enabled: stopped,
    retry: false,
    queryFn: () => checkSample(presetId, version, secret || undefined),
  });

  const movable = usage.filter((row) => !row.isCurrent);

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="bg-ghost text-primary"
        onClick={() => setOpen(true)}
      >
        Move sources to v{version}…
      </Button>

      <Modal
        open={open}
        onOpenChange={setOpen}
        width="lg"
        title={`Move sources to v${version}`}
        description="Moving is an edit on those sources, not on this scheme. It changes how real incoming requests are checked, so you pick which sources go."
        submit={
          stopped
            ? {
                label: 'Move anyway',
                onSubmit: () => move.mutate(true),
                disabled: move.isPending,
                waiting: move.isPending,
              }
            : {
                label: `Move ${picked.length > 0 ? picked.length : ''}`,
                onSubmit: () => move.mutate(false),
                disabled: picked.length === 0 || move.isPending,
                waiting: move.isPending,
              }
        }
      >
        <div className="flex flex-col gap-3">
          {movable.length === 0 ? (
            <p className="text-data text-muted">
              Nothing to move: every auth method using this scheme is already on v{version}.
            </p>
          ) : (
            <div className="flex flex-col gap-1">
              {movable.map((row) => (
                <Checkbox
                  key={row.sourceAuthMethodId}
                  className="h-row-lg w-full rounded-item px-2 hover:bg-hover-raised"
                  checked={picked.includes(row.sourceAuthMethodId)}
                  onCheckedChange={(next) =>
                    setPicked(
                      next
                        ? [...picked, row.sourceAuthMethodId]
                        : picked.filter((id) => id !== row.sourceAuthMethodId),
                    )
                  }
                  label={
                    <span className="flex min-w-0 grow items-center gap-inset">
                      <span className="min-w-0 grow truncate">
                        {nameOf(row.sourceId) ?? <ShortId id={row.sourceId} />}
                      </span>
                      <span className="shrink-0 font-mono text-micro text-faint">
                        on v{row.version}
                      </span>
                    </span>
                  }
                />
              ))}
            </div>
          )}

          <div>
            <FieldLabel htmlFor="move-secret">
              A candidate secret, so the re-check reaches the signature
            </FieldLabel>
            <Input
              id="move-secret"
              size="row"
              mono
              type="password"
              autoComplete="off"
              placeholder="whsec_…"
              value={secret}
              onChange={(event) => setSecret(event.target.value)}
            />
            <p className="mt-label text-meta leading-17 text-muted">
              Never stored. Without it the re-check proves only that this version still reads the
              stored sample.
            </p>
          </div>

          {stopped && (
            <>
              <Banner detail={move.error instanceof ApiError ? move.error.code : undefined}>
                {move.error instanceof Error ? move.error.message : ''}
              </Banner>
              {recheck.data && (
                <div className="rounded-panel bg-surface-sunken px-input py-3">
                  <VerifySteps steps={verifySteps(recheck.data, spec ?? null)} />
                </div>
              )}
              <Note tone="danger">
                A sample goes stale, and a hard refusal would lock you out of fixing the scheme — so
                the move can still go ahead, asked for and on the record.
              </Note>
            </>
          )}

          {move.isError && !stopped && (
            <Banner detail={move.error instanceof ApiError ? move.error.code : undefined}>
              {move.error instanceof Error ? move.error.message : 'The move was refused.'}
            </Banner>
          )}
        </div>
      </Modal>
    </>
  );
};
